import React, { useState, useEffect, useTransition } from 'react';
import { MdTune, MdClose } from 'react-icons/md';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

interface FilterProps {
  onTaskSelect: (taskId: string | null, taskName: string) => void;
  onClientSelect: (clientIds: string[], clientNames: string[]) => void;
}

const Filter: React.FC<FilterProps> = ({ onTaskSelect, onClientSelect }) => {
  const { t } = useTranslation('client');
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTask, setSelectedTask] = useState<{
    id: string | null;
    name: string;
  }>({ id: null, name: 'All Tasks' });
  const [selectedClients, setSelectedClients] = useState<
    { id: string; name: string }[]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDisplay, setFilterDisplay] = useState('Filter');

  const [tasks, setTasks] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);

  const [tempTask, setTempTask] = useState(selectedTask);
  const [tempClients, setTempClients] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await axiosInstance.get('/v1/tasks/');
        setTasks(response.data.results);
      } catch (error) {
        console.error('Failed to fetch tasks', error);
      }
    };

    const fetchClients = async () => {
      try {
        const response = await axiosInstance.get('/v1/client_deliveries/');
        setClients(response.data.results);
      } catch (error) {
        console.error('Failed to fetch clients', error);
      }
    };

    fetchTasks();
    fetchClients();
  }, []);

  useEffect(() => {
    const formatList = (list: { name: string }[], maxItems: number): string => {
      if (list.length <= maxItems) {
        return list.map((item) => item.name).join(', ');
      }
      return `${list
        .slice(0, maxItems)
        .map((item) => item.name)
        .join(', ')}...`;
    };

    if (selectedTask.name === 'All Tasks' && selectedClients.length === 0) {
      setFilterDisplay('Filter');
    } else {
      const taskDisplay =
        selectedTask.name === 'All Tasks' ? '' : selectedTask.name;
      const clientsDisplay =
        selectedClients.length === 0 ? '' : formatList(selectedClients, 5);
      setFilterDisplay(
        [taskDisplay, clientsDisplay].filter(Boolean).join(', ') || 'Filter'
      );
    }
  }, [selectedTask, selectedClients]);

  const togglePopup = () => {
    setShowPopup(!showPopup);
  };

  const handleTaskChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = tasks.find((task) => task.name === event.target.value);
    setTempTask({ id: selected?.id || null, name: event.target.value });
  };

  const handleClientChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const selected = clients.find((client) => client.name === value);

    setTempClients((prevClients) =>
      prevClients.some((client) => client.name === value)
        ? prevClients.filter((client) => client.name !== value)
        : [...prevClients, { id: selected.id, name: value }]
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const deselectAllClients = () => {
    setTempClients([]);
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const applyFilters = () => {
    setSelectedTask(tempTask);
    setSelectedClients(tempClients);
    onTaskSelect(tempTask.id, tempTask.name);
    onClientSelect(
      tempClients.map((client) => client.id),
      tempClients.map((client) => client.name)
    );
    togglePopup();
  };

  const discardChanges = () => {
    setSelectedTask({ id: null, name: 'All Tasks' });
    setSelectedClients([]);
    setTempTask({ id: null, name: 'All Tasks' });
    setTempClients([]);
    setSearchQuery('');
    setShowPopup(false);
    onTaskSelect(null, 'All Tasks');
    onClientSelect([], []);
  };

  return (
    <div className="relative flex p-2 justify-center items-center border-r px-4 mr-3">
      <div className="flex flex-row cursor-pointer items-center">
        <button
          onClick={togglePopup}
          className={`flex items-center px-4 py-2 rounded-l-md ${
            filterDisplay === 'Filter'
              ? 'text-gray-400'
              : 'text-primary/300 bg-primary/50'
          }`}
        >
          {filterDisplay}
        </button>
        {filterDisplay === 'Filter' ? (
          <MdTune className="text-gray-400" onClick={togglePopup} />
        ) : (
          <div className="py-3 px-1 bg-primary/50 rounded-r-md text-primary/300">
            <MdClose className="" onClick={discardChanges} />
          </div>
        )}
      </div>
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 text-black">
          <div className="bg-white w-1/2 max-h-4/5 shadow-lg border rounded z-10 overflow-auto">
            <h6 className="text-xl font-semibold mb-4 px-8 py-4 text-subtitle-1">
              {t('filter.title')}
            </h6>
            <div className="border-t overflow-auto max-h-1/2">
              <h6 className="text-lg font-medium mb-2 px-8 py-2 text-gray-400 text-subtitle-2">
                {t('filter.by_task')}
              </h6>
              <div className="px-8 py-2">
                <div className="grid grid-cols-4 gap-2">
                  <label className="block mb-2">
                    <input
                      className="mr-2"
                      type="radio"
                      name="task"
                      value="All Tasks"
                      checked={tempTask.name === 'All Tasks'}
                      onChange={handleTaskChange}
                    />
                    {t('filter.all_task')}
                  </label>
                  {tasks.map((task) => (
                    <label key={task.id} className="block mb-2">
                      <input
                        className="mr-2"
                        type="radio"
                        name="task"
                        value={task.name}
                        checked={tempTask.name === task.name}
                        onChange={handleTaskChange}
                      />
                      {task.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-b overflow-auto max-h-1/2">
              <div className="flex flex-row justify-between items-center">
                <h6 className="text-4xl font-medium px-8 py-4">
                  {t('filter.by_client')}
                </h6>
                <button
                  onClick={deselectAllClients}
                  className="px-8 underline text-primary/200 rounded-md"
                >
                  {t('filter.deselect_all')}
                </button>
              </div>
              <div className="px-8 py-2">
                <input
                  type="text"
                  placeholder={t('filter.client_placeholder')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="border w-full rounded-md py-2 mb-4 text-left px-2"
                />
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <label key={client.id} className="block mb-2">
                      <input
                        className="mr-2"
                        type="checkbox"
                        value={client.name}
                        checked={tempClients.some(
                          (c) => c.name === client.name
                        )}
                        onChange={handleClientChange}
                      />
                      {client.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 px-8 py-4">
              <button
                onClick={discardChanges}
                className="flex-1 px-4 py-2 bg-gray-300 rounded-md"
              >
                {t('action.close')}
              </button>
              <button
                onClick={applyFilters}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md"
              >
                {t('filter.title')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Filter;
